import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import { Box, IconButton, Avatar as MuiAvatar, Typography } from '@mui/material';
import { Tooltip } from '../Tooltip';
import { HelixPalette, HelixStyles } from '../../helix';
import { OverflowTooltip } from '../Tooltip';
/**
 * A simple Avatar to show a user
 */
export const Avatar = ({ dataId, user, customTooltip, displayName = false, fieldLabel, hyperlink, isClickable = false, onClick, showTooltip = true, size = 'md', subtitle, tag, style, }) => {
    // Constants.
    const dataIdPrefix = `${dataId}-avatar`;
    let dims;
    let fontSize;
    const { image, name } = user;
    let backgroundColor = undefined;
    let color = undefined;
    const defaultOverlay = {
        content: '""',
        display: 'block',
        position: 'absolute',
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        backgroundColor: HelixPalette.white,
        opacity: 0.3,
    };
    /**
     * Helper function to generate a random string based on the user's name.
     * @param user The user object to be displayed.
     * @returns An HTML colour code.
     */
    const getBackgroundColor = (user) => {
        const { name } = user;
        // Continue if there is a name to work from
        let hash = 0;
        let i;
        // eslint-disable-next-line eslint-comments/disable-enable-pair
        /* eslint-disable no-bitwise */
        for (i = 0; i < name.length; i += 1) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
        }
        let color = '#';
        for (i = 0; i < 3; i += 1) {
            const value = (hash >> (i * 8)) & 0xff;
            color += `00${value.toString(16)}`.slice(-2);
        }
        return color;
    };
    /**
     * Based on the background colour returns the correct text colour.
     * @param hexcolor The background hex code.
     * @returns The text colour.
     */
    const getTextColor = (hexcolor) => {
        // If a leading # is provided, remove it
        const parsedColor = hexcolor.replace('#', '');
        // Convert to RGB value
        const r = parseInt(parsedColor.substring(0, 2), 16);
        const g = parseInt(parsedColor.substring(2, 4), 16);
        const b = parseInt(parsedColor.substring(4, 6), 16);
        // Get YIQ ratio
        const yiq = (r * 299 + g * 587 + b * 114) / 1000;
        // Check contrast
        return yiq >= 128 ? HelixPalette.neutral100 : HelixPalette.white;
    };
    /**
     * Helper function to get the initials to display on an Avatar.
     * @param user User object to display
     * @returns Empty string if no values for name, else initials
     */
    const getUserInitials = ({ name }) => {
        const trimmedName = name.trim().replace(/^-+|-+$/g, '');
        const parts = trimmedName.split(/[\s-]+/);
        const first = parts[0][0];
        let second;
        let third;
        if (parts.length > 2) {
            second = parts[parts.length - 2][0];
            third = parts[parts.length - 1][0];
            return `${first}${second}${third}`;
        }
        else if (parts.length > 1) {
            second = parts[parts.length - 1][0];
            return `${first}${second}`;
        }
        else {
            return first;
        }
    };
    // If there is no image then set the background and text colors.
    if (!image) {
        backgroundColor = getBackgroundColor(user);
        color = getTextColor(backgroundColor);
    }
    const initials = getUserInitials(user);
    // Set dimensions and font size based on size prop and number of initials.
    const threeInitials = (initials === null || initials === void 0 ? void 0 : initials.length) > 2;
    switch (size) {
        case 'sm':
            dims = 24;
            fontSize = threeInitials ? '6px' : '10px';
            break;
        case 'lg':
            dims = 56;
            fontSize = threeInitials ? '16px' : '22px';
            break;
        case 'xl':
            dims = 80;
            fontSize = threeInitials ? '22px' : '30px';
            break;
        default:
            dims = 40;
            fontSize = threeInitials ? '10px' : '16px';
            break;
    }
    //Set avatar equal to a default avatar
    let avatar = (_jsx(MuiAvatar, Object.assign({ "aria-label": initials, "data-id": `${dataIdPrefix}`, alt: name, src: image, tabIndex: showTooltip && !isClickable ? 0 : undefined, sx: Object.assign(Object.assign({ backgroundColor,
            color, border: `solid ${size == 'sm' ? '1px' : '2px'} ${image ? 'transparent' : HelixPalette.white}`, height: dims, width: dims, margin: '0' }, ((showTooltip || isClickable) && {
            ':hover': Object.assign(Object.assign({}, (image && { borderColor: HelixPalette.teal70 })), { '::after': Object.assign({}, defaultOverlay) }),
            ':focus-visible': {
                outline: 'none',
                borderColor: HelixPalette.blue70,
                backgroundColor: HelixPalette.white,
                color: HelixPalette.blue70,
            },
            '&[aria-current="true"]': {
                borderColor: image ? HelixPalette.white : 'transparent',
                backgroundColor: HelixPalette.white,
                color: HelixPalette.teal80,
            },
        })), style) }, { children: _jsx(Typography, Object.assign({}, HelixStyles.textBase, { "data-id": `${dataIdPrefix}-initials`, color: "inherit", component: "span", fontSize: fontSize, lineHeight: "1", fontWeight: "bold", textTransform: "uppercase" }, { children: initials })) }), `${dataIdPrefix}`));
    //Set avatar equal to an avatar wrapped in a button if clickable
    if (isClickable) {
        avatar = (_jsx(IconButton, Object.assign({ onClick: onClick, disableFocusRipple: true, disableRipple: true, sx: {
                padding: 0,
                ':active:focus-within': {
                    '& .MuiAvatar-root': {
                        borderColor: image ? HelixPalette.teal90 : HelixPalette.white,
                        '::after': Object.assign(Object.assign({}, defaultOverlay), { backgroundColor: HelixPalette.black, opacity: 0.2 }),
                    },
                },
                ':focus-visible': {
                    '& .MuiAvatar-root': {
                        outline: 'none',
                        borderColor: HelixPalette.blue70,
                        backgroundColor: HelixPalette.white,
                        color: HelixPalette.blue70,
                    },
                },
            } }, { children: avatar }), `avatar`));
    }
    //Set avatar equal to an avatar wrapped in a tooltip if tooltip is shown
    if (showTooltip) {
        avatar = (_jsx(Tooltip, Object.assign({ title: customTooltip !== null && customTooltip !== void 0 ? customTooltip : name }, { children: avatar }), `avatar`));
    }
    if (fieldLabel || displayName || hyperlink || subtitle || tag) {
        return (_jsxs(Box, Object.assign({ overflow: "hidden" }, { children: [fieldLabel && (_jsx(Typography, Object.assign({}, HelixStyles.style_07, { marginBottom: "4px" }, { children: fieldLabel }))), _jsxs(Box, Object.assign({ display: "flex", flexDirection: "row", alignItems: "center" }, { children: [avatar, _jsxs(Box, Object.assign({ overflow: "hidden", marginLeft: "8px" }, { children: [_jsxs(Box, Object.assign({ display: "flex", flexDirection: "row" }, { children: [displayName && (_jsx(OverflowTooltip, Object.assign({ dataId: dataIdPrefix, title: name, marginRight: "8px" }, HelixStyles.style_06_compact, { children: name }))), tag] })), subtitle && (_jsx(OverflowTooltip, Object.assign({ dataId: dataIdPrefix, title: subtitle }, HelixStyles.style_07, { color: HelixPalette.neutral70 }, { children: subtitle }))), hyperlink && React.cloneElement(hyperlink, { style: { padding: '0' } })] }))] }))] })));
    }
    return avatar;
};
export default Avatar;
